/**
 * 로그인 페이지
 */

import React, { useEffect } from 'react';
import { Form, Input, Button, Card, Typography, Alert, Space } from 'antd';
import { UserOutlined, LockOutlined } from '@ant-design/icons';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../../hooks/useAuth';
import { LoginRequest } from '../../types/auth';
import './AuthPages.css';

const { Title, Text } = Typography;

/**
 * 로그인 페이지 컴포넌트
 */
export const LoginPage: React.FC = () => {
  const navigate = useNavigate();
  const { login, isAuthenticated, isLoading, error, navigateToDashboard } = useAuth();
  const [form] = Form.useForm();

  // 이미 로그인된 경우 대시보드로 이동
  useEffect(() => {
    if (isAuthenticated) {
      navigateToDashboard();
    }
  }, [isAuthenticated, navigateToDashboard]);

  /**
   * 로그인 제출 핸들러
   */
  const handleSubmit = async (values: LoginRequest) => {
    try {
      const user = await login(values);

      // 로그인 성공 → 역할별 리다이렉트
      switch (user.role) {
        case 'admin':
          navigate('/system', { replace: true });
          break;
        case 'group_admin':
        case 'instructor':
          navigate('/dashboard', { replace: true });
          break;
        case 'student':
          navigate('/workbooks', { replace: true });
          break;
        default:
          navigate('/dashboard', { replace: true });
      }
    } catch (err) {
      // 에러는 useAuth에서 처리됨
      console.error('Login failed:', err);
    }
  };

  return (
    <div className="auth-container">
      <Card className="auth-card" variant="borderless">
        <Space direction="vertical" size="large" style={{ width: '100%' }}>
          {/* 로고 및 타이틀 - 클릭 시 랜딩페이지로 이동 */}
          <div className="auth-header">
            <Title
              level={2}
              style={{
                margin: 0,
                textAlign: 'center',
                cursor: 'pointer',
              }}
              onClick={() => navigate('/')}
            >
              📚 채점톡
            </Title>
            <Text type="secondary" style={{ textAlign: 'center', display: 'block' }}>
              수학 자동채점 시스템
            </Text>
          </div>

          {/* 에러 메시지 */}
          {error && (
            <Alert
              message="로그인 실패"
              description={error}
              type="error"
              showIcon
              closable
            />
          )}

          {/* 로그인 폼 */}
          <Form
            form={form}
            name="login"
            onFinish={handleSubmit}
            autoComplete="off"
            size="large"
            layout="vertical"
          >
            <Form.Item
              name="email"
              rules={[
                { required: true, message: '이메일을 입력해주세요' },
                { type: 'email', message: '올바른 이메일 형식이 아닙니다' },
              ]}
            >
              <Input
                prefix={<UserOutlined />}
                placeholder="이메일"
                autoComplete="email"
              />
            </Form.Item>

            <Form.Item
              name="password"
              rules={[{ required: true, message: '비밀번호를 입력해주세요' }]}
            >
              <Input.Password
                prefix={<LockOutlined />}
                placeholder="비밀번호"
                autoComplete="current-password"
              />
            </Form.Item>

            <Form.Item>
              <Button
                type="primary"
                htmlType="submit"
                loading={isLoading}
                block
                style={{ marginTop: '8px' }}
              >
                로그인
              </Button>
            </Form.Item>
          </Form>

          {/* 하단 링크 */}
          <div className="auth-footer">
            <Space split="|" size="middle">
              <Link to="/signup">
                <Text>회원가입 (학원 관리자)</Text>
              </Link>
              <Link to="/reset-password">
                <Text>비밀번호 찾기</Text>
              </Link>
            </Space>
          </div>
        </Space>
      </Card>
    </div>
  );
};

export default LoginPage;
